import * as React from 'react';
import { connect } from 'react-redux';
import { Modal, Button, Tab, Nav, NavItem, Row, Col } from 'react-bootstrap';
import { Translate } from '../../shared/LanguageService';
import CopywriterForm from './CopywritersForm';
import FormInputChangeHandler from '../../shared/form/FormInputChangeHandler';
import * as Actions from './actions/CopywriterActions';
import { ICopywriter, ICopywriterLanguage } from './Entities';
import notify from '../../shared/feedback/notifications';
import { assign } from '../../shared/Assign';
import { handleError } from '../../shared/ErrorCatching';

interface IProps {
    showModal: boolean;
    toggleModal: () => void;
    copywriterId: number;
    availableLanguagesKeys: ICopywriterLanguage[];
    actions: any;
}

interface IState {
    errors: {};
    copywriter?: ICopywriter;
    creating: boolean;
    isLoading: boolean;
}

class CopywriterModal extends React.Component<IProps, IState> {
    formInputChangeHandler: FormInputChangeHandler;

    defaultCopywriter = {
        id: -1,
        name: '',
        email: '',
        languageKey: 'sv',
        role: '',
        phoneNumber: '',
        profileImageReference: '',
        username: '',
        isRemoved: false
    };

    constructor(props: IProps, context: any) {
        super(props);
        this.state = {
            errors: {},
            creating: false,
            isLoading: false,
            copywriter: this.defaultCopywriter
        };

        this.save = this.save.bind(this);
        this.save = this.save.bind(this);
        this.onChange = this.onChange.bind(this);
    }

    componentDidUpdate(prevProps: IProps, prevState) {
        if (prevProps.copywriterId !== this.props.copywriterId) {
            if (this.props.copywriterId > 0) {
                this.setState({ isLoading: true });
                this.props.actions.getCopywriter(this.props.copywriterId).then((result) => {
                    this.setState({ copywriter: result });
                });
            }
        }
    }

    save() {
        const { copywriter } = this.state;

        const user = {
            ...copywriter,
            email:
                !copywriter.email || copywriter.email.length < 1
                    ? copywriter.username
                    : copywriter.email
        };

        this.setState({ creating: true, copywriter: user }, () => {
            if (copywriter.id > 0) {
                this.props.actions
                    .update(this.state.copywriter)
                    .then((result) => {
                        this.setState({ creating: false });
                        this.props.toggleModal();
                        notify.success(Translate('copywriter/notification/updated'));
                    })
                    .catch((error: any) =>
                        handleError(error, (errors: any): void => {
                            this.setState({ errors: errors, creating: false });
                        })
                    );
            } else {
                this.props.actions
                    .create(this.state.copywriter)
                    .then((result) => {
                        this.setState({ creating: false });
                        this.props.toggleModal();
                        notify.success(Translate('copywriter/notification/added'));
                    })
                    .then(() => {
                        this.setState({copywriter: this.defaultCopywriter});
                    })
                    .catch((error: any) =>
                        handleError(error, (errors: any): void => {
                            this.setState({ errors: errors, creating: false });
                        })
                    );
            }
        });
    }

    onChange(e) {
        const copywriter = assign({}, this.state.copywriter);

        const value = e.target.value;

        if (e.target.name === 'role') {
            copywriter.roleId = value;
        } else {
            copywriter[e.target.name] = value;
        }

        this.setState({ copywriter: copywriter });
    }

    setTabValidationError(...properties: string[]) {
        if (!this.state.errors || Object.keys(this.state.errors).length === 0) {
            return null;
        }

        let hasErrors = properties.some((prop: string) => {
            return this.state.errors[prop] !== undefined;
        });

        return hasErrors ? (
            <i className="material-icons material-icons-danger material-icons-label md-18">error</i>
        ) : null;
    }

    close = () => {
        this.setState({ copywriter: this.defaultCopywriter });
        this.props.toggleModal();
    };

    render() {
        return (
            <Modal show={this.props.showModal} onHide={this.props.toggleModal}>
                <Modal.Header closeButton>
                    {this.props.copywriterId === null
                        ? Translate('copywriter/modal/header/add-copywriter')
                        : Translate('copywriter/modal/header/edit-copywriter')}
                </Modal.Header>
                <Modal.Body>
                    <form>
                        <Tab.Container defaultActiveKey={1} id="uncontrolled-tab">
                            <div>
                                <Nav>
                                    <NavItem eventKey={1}>
                                        {Translate('copywriter/modal/tab/copywriter')}
                                        {this.setTabValidationError(
                                            'name',
                                            'username',
                                            'email',
                                            'phoneNumber',
                                            'languageKey',
                                            'stylingFirmId',
                                            'role'
                                        )}
                                    </NavItem>
                                </Nav>
                                <Tab.Content>
                                    <Tab.Pane eventKey={1}>
                                        <fieldset>
                                            <legend>
                                                {Translate('copywriter/modal/tab/copywriter')}
                                            </legend>
                                            <Row>
                                                <Col md={8}>
                                                    <CopywriterForm
                                                        errors={this.state.errors}
                                                        input={this.state.copywriter}
                                                        onChange={this.onChange}
                                                        availableLanguages={
                                                            this.props.availableLanguagesKeys
                                                        }
                                                    />
                                                </Col>
                                            </Row>
                                        </fieldset>
                                    </Tab.Pane>
                                </Tab.Content>
                            </div>
                        </Tab.Container>
                    </form>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={this.close}>
                        {Translate('copywriter/modal/footer-button/close')}
                    </Button>
                    <Button bsStyle="primary" disabled={this.state.creating} onClick={this.save}>
                        {this.state.creating
                            ? Translate('copywriter/modal/footer-button/saving')
                            : Translate('copywriter/modal/footer-button/save')}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

function mapDispatchToProps(dispatch: any) {
    return {
        actions: {
            getCopywriter: (id: number) => dispatch(Actions.getCopywriter(id)),
            create: (copywriter: ICopywriter) => dispatch(Actions.createCopywriter(copywriter)),
            update: (copywriter: ICopywriter) => dispatch(Actions.updateCopywriter(copywriter))
        }
    };
}

function mapStateToProps(state: any) {
    return {};
}

export default connect(mapStateToProps, mapDispatchToProps)(CopywriterModal);
